import {
  not,
  all,
  allPass,
  anyPass,
  applySpec,
  complement,
  compose,
  contains,
  find,
  has,
  identity,
  isNil,
  join,
  lt,
  max,
  path,
  pathOr,
  prop,
  propEq,
  reduce,
  split,
  values,
  without,
  whereEq,
} from 'ramda';
import { createSelector } from 'reselect';
import { CLAIM_STATE, CLAIM_STATUS, CLAIM_SUBSTATUS, HISTORY_EVENTS, MISSION_STATUS } from '../enums';
import { formatIntPhoneNumber } from '../utils/normalizing';
import { getIsPublicApiV1 } from './settings';

const { ACTIVE } = CLAIM_STATE;
const { OPENED, CANCELLED, CLOSED, OTHER, DENIED } = CLAIM_STATUS;
const {
  CLAIM_CORRECTION,
  DUPLICATED_CLAIM,
  POSSIBLE_REQUEST,
  IN_PROGRESS,
  ERROR_OR_INTERNAL_TEST,
  UNDER_INVESTIGATION,
  CUSTOMER_NO_LONGER_WANTS_SERVICE,
  ASSISTANCE_DELIVERED,
  ASSISTANCE_CANCELLED,
  TRANSFERRED_TO_OEM,
  CONTROL_LIST,
  NOT_COVERED,
} = CLAIM_SUBSTATUS;

export const APPROVED = 'APPROVED';
export const IDENTIFIED = 'IDENTIFIED';
export const AUTHENTICATED = 'AUTHENTICATED';

const hasScope = name =>
  compose(
    contains(name),
    split(' '),
    pathOr('', ['scope']),
  );

const ROOT = 'coreApi';

export const getCoreApi = prop(ROOT);
const createCoreApiSelector = (...params) =>
  createSelector(
    getCoreApi,
    ...params,
  );
export const getIsFetching = createCoreApiSelector(state => Boolean(state.pendingRequests));

const getRetryCounter = path([ROOT, 'retryCounter']);
export const getMaxRetryCounter = createSelector(
  getRetryCounter,
  compose(
    reduce(max, 0),
    values,
  ),
);

export const getRetryNotFoundModal = path([ROOT, 'retryNotFoundModal']);
export const getRetryExpiredModal = path([ROOT, 'retryExpiredModal']);
export const getClaimAlreadyExists = path([ROOT, 'retryClaimExist']);

export const getError = path([ROOT, 'lastError']);
export const getErrorCode = createSelector(
  getError,
  prop('errorCode'),
);

export const getAccessToken = path([ROOT, 'accessToken']);
export const getRefreshToken = path([ROOT, 'refreshToken']);
export const getTokenType = path([ROOT, 'tokenType']);
export const getScope = path([ROOT, 'scope']);
export const getExpiresAt = path([ROOT, 'expiresAt']);
export const getJwt = path([ROOT, 'jwt']);
export const getCarDestinations = path([ROOT, 'carDestinations']);
export const getCarDestinationsRules = path([ROOT, 'carDestinationsRules']);
export const getPreviousClaimId = path([ROOT, 'previousClaimId']);
export const getPreviousMissionId = path([ROOT, 'previousMissionId']);

// Claim + Mission History
const getClaimHistoryApi = path([ROOT, 'claimHistoryCurrent']);

export const getClaimHistory = createSelector(
  getClaimHistoryApi,
  prop('claimHistory'),
);
export const getMissionHistory = createSelector(
  getClaimHistoryApi,
  prop('missionHistory'),
);

export const getMissions = pathOr({}, [ROOT, 'missions']);
export const getMission = pathOr({}, [ROOT, 'mission']);
export const getCoreRentalMission = pathOr({}, [ROOT, 'rentalMission']);

export const getSpecificDestinationsLoaded = pathOr({}, [ROOT, 'specificDestinationsLoaded']);
export const getMissionService = createSelector(
  getMission,
  prop('service'),
);
export const getMissionProviderCoordinates = createSelector(
  getMission,
  mission => {
    const coordinatesFromProvider = path(['provider', 'location', 'position'], mission);
    if (coordinatesFromProvider) {
      return coordinatesFromProvider;
    }

    return {
      lng: pathOr(null, ['payload', 'provider', 'lng'], mission),
      lat: pathOr(null, ['payload', 'provider', 'lat'], mission),
    };
  },
);
export const getAppointment = createSelector(
  getMission,
  getIsPublicApiV1,
  (mission, isApiPublicV1) => (isApiPublicV1 ? prop('pickupTime', mission) : prop('appointment', mission)),
);
export const getMissionStatus = createSelector(
  getMission,
  prop('status'),
);
export const getMissionIsInCreation = createSelector(
  getMission,
  mission => prop('status', mission) === MISSION_STATUS.IN_CREATION,
);
export const getMissionId = createSelector(
  getMission,
  prop('id'),
);

export const getLastCancelledMissionId = pathOr({}, [ROOT, 'lastCancelledMissionId']);

export const getCoreRentalMissionId = createSelector(
  getCoreRentalMission,
  prop('id'),
);

export const getCoreRentalMissionStatus = createSelector(
  getCoreRentalMission,
  prop('status'),
);

export const getCoreRentalMissionPickupTime = createSelector(
  getCoreRentalMission,
  prop('rentalPickupTime'),
);

export const getCoreRentalMissionDuration = createSelector(
  getCoreRentalMission,
  prop('rentalDuration'),
);

export const getCoreRentalIsEligibleForExtension = createSelector(
  getCoreRentalMission,
  prop('isEligibleForExtension'),
);

export const getRentalBookingDetailsUpdate = createSelector(
  getClaimHistoryApi,
  getCoreRentalMissionId,
  (claimHistoryApi, coreRentalMissionId) => {
    if (!claimHistoryApi) {
      return { firstRentalBookingModifier: null, lastRentalBookingModifier: null };
    }

    const { missionHistory } = claimHistoryApi;

    const filteredHistory = missionHistory.filter(
      entry => entry.missionId === coreRentalMissionId && entry.type === HISTORY_EVENTS.RENTAL_BOOKING_DETAILS_UPDATED,
    );

    const sortedHistory = filteredHistory.sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));

    const firstRentalBookingModifier = sortedHistory[0] || null;
    const lastRentalBookingModifier = sortedHistory[sortedHistory.length - 1] || null;
    if (firstRentalBookingModifier === lastRentalBookingModifier) {
      return { firstRentalBookingModifier, lastRentalBookingModifier: null };
    }
    return { firstRentalBookingModifier, lastRentalBookingModifier };
  },
);

export const getMissionType = createSelector(
  getMission,
  prop('missionType'),
);

export const getMissionProviderName = createSelector(
  getMission,
  path(['provider', 'name']),
);
export const getMissionEta = createSelector(
  getMission,
  mission => {
    const eta = path(['eta'], mission);
    if (eta) {
      return eta;
    }

    return pathOr({}, ['payload', 'eta'], mission);
  },
);

export const getLastEtaUpdate = createSelector(
  getMission,
  path(['payload', 'lastEtaUpdate']),
);

export const getPickupTime = createSelector(
  getMission,
  mission => {
    const pickupTime = path(['payload', 'pickupTime'], mission);
    if (pickupTime) {
      return pickupTime;
    }

    return pathOr(null, ['pickupTime'], mission);
  },
);

export const getMissionCurrentLocation = createSelector(
  getMission,
  path(['payload', 'currentLocation']),
);

export const getCarDestination = createSelector(
  getMission,
  getIsPublicApiV1,
  (mission, isApiPublicV1) => (isApiPublicV1 ? prop('vehicleDestination', mission) : prop('carDestination', mission)),
);
export const getCarDestinationType = createSelector(
  getCarDestination,
  prop('type'),
);
export const getCarDestinationProviderName = createSelector(
  getCarDestination,
  getIsPublicApiV1,
  (carDestination, isApiPublicV1) =>
    isApiPublicV1 ? path(['provider', 'name'], carDestination) : prop('name', carDestination),
);
export const getCarDestinationProviderContact = createSelector(
  getCarDestination,
  getIsPublicApiV1,
  (carDestination, isApiPublicV1) =>
    isApiPublicV1 ? path(['provider', 'contact', 'phone1'], carDestination) : prop('phone1', carDestination),
);

export const getCarDestinationService = createSelector(
  getCarDestination,
  prop('service'),
);
export const getCarDestinationAddress = createSelector(
  getCarDestination,
  getIsPublicApiV1,
  (carDestination, isApiPublicV1) =>
    isApiPublicV1 ? path(['provider', 'location', 'address'], carDestination) : prop('address', carDestination),
);
export const getCarDestinationOpeningHours = createSelector(
  getCarDestination,
  getIsPublicApiV1,
  (carDestination, isApiPublicV1) =>
    isApiPublicV1 ? path(['provider', 'openingHours'], carDestination) : prop('openingHours', carDestination),
);

export const getCarDestinationFullAddress = createSelector(
  getCarDestinationAddress,
  address => join(' ', without([undefined], values(address))),
);

export const getMissionMonitoring = path([ROOT, 'missionMonitoring']);
export const getMissionMonitoringStatus = createSelector(
  getMissionMonitoring,
  prop('status'),
);

export const getClaim = path([ROOT, 'claim']);
export const getClaimId = createSelector(
  getClaim,
  prop('id'),
);
export const getClaimState = createSelector(
  getClaim,
  prop('claimState'),
);
export const getClaimCreationDate = createSelector(
  getClaim,
  prop('creationDate'),
);
export const getClaimStatus = createSelector(
  getClaim,
  prop('status'),
);
export const getClaimSubstatus = createSelector(
  getClaim,
  prop('substatus'),
);
export const getIsClaimInProgress = createSelector(
  getClaimState,
  getClaimStatus,
  getClaimSubstatus,
  (claimState, status, substatus) =>
    whereEq({ claimState: ACTIVE, status: OPENED, substatus: IN_PROGRESS })({ claimState, status, substatus }),
);
export const getIsClaimNotDisplayed = createSelector(
  getClaimState,
  getClaimStatus,
  getClaimSubstatus,
  (claimState, status, substatus) =>
    anyPass([
      compose(
        not,
        propEq('claimState', ACTIVE),
      ),
      whereEq({ status: OPENED, substatus: CLAIM_CORRECTION }),
      whereEq({ status: CANCELLED, substatus: DUPLICATED_CLAIM }),
      whereEq({ status: CLOSED, substatus: POSSIBLE_REQUEST }),
      whereEq({ status: OTHER, substatus: ERROR_OR_INTERNAL_TEST }),
    ])({ claimState, status, substatus }),
);

export const getIsNonEditableClaim = createSelector(
  getClaimState,
  getClaimStatus,
  getClaimSubstatus,
  (claimState, status, substatus) =>
    anyPass([
      whereEq({ claimState: ACTIVE, status: OPENED, substatus: UNDER_INVESTIGATION }),
      whereEq({ claimState: ACTIVE, status: CANCELLED, substatus: CUSTOMER_NO_LONGER_WANTS_SERVICE }),
      whereEq({ claimState: ACTIVE, status: CLOSED, substatus: ASSISTANCE_DELIVERED }),
      whereEq({ claimState: ACTIVE, status: CLOSED, substatus: ASSISTANCE_CANCELLED }),
      whereEq({ claimState: ACTIVE, status: CLOSED, substatus: TRANSFERRED_TO_OEM }),
      whereEq({ claimState: ACTIVE, status: DENIED, substatus: CONTROL_LIST }),
      whereEq({ claimState: ACTIVE, status: DENIED, substatus: NOT_COVERED }),
    ])({ claimState, status, substatus }),
);

export const getAffiliate = createSelector(
  getClaim,
  prop('affiliate'),
);
export const getSubscription = createSelector(
  getClaim,
  prop('subscription'),
);
export const getLocation = createSelector(
  getClaim,
  prop('location'),
);
export const getClaimPosition = createSelector(
  getLocation,
  getIsPublicApiV1,
  (location, isApiPublicV1) => (isApiPublicV1 ? location?.position : location),
);
export const getClaimAddress = createSelector(
  getClaim,
  getLocation,
  getIsPublicApiV1,
  (claim, location, isApiPublicV1) => (isApiPublicV1 ? location?.address : claim?.address),
);
export const getClaimEventDate = createSelector(
  getClaim,
  prop('eventDate'),
);
export const getFormattedVehicleAddress = createSelector(
  getClaimAddress,
  prop('formattedAddress'),
);
export const getCountry = createSelector(
  getClaimAddress,
  prop('country'),
);

export const getPosition = createSelector(
  getLocation,
  getIsPublicApiV1,
  (location, isApiPublicV1) => (isApiPublicV1 ? location?.position : location),
);
export const getLocationDetails = createSelector(
  getClaim,
  prop('locationDetails'),
);
export const getEventDate = createSelector(
  getClaim,
  prop('eventDate'),
);
export const getHighwayLocation = createSelector(
  getLocationDetails,
  prop('highwayLocation'),
);

export const getHighwayLocationV1 = createSelector(
  getLocation,
  prop('highwayLocation'),
);

export const getClaimHighwayMetres = createSelector(
  getHighwayLocation,
  getHighwayLocationV1,
  getIsPublicApiV1,
  (highwayLocationV0, highwayLocationV1, isApiPublicV1) => {
    if (isApiPublicV1) return highwayLocationV1?.milestone;
    return highwayLocationV0?.metres;
  },
);
export const getHasHighwayDirection = createSelector(
  getHighwayLocation,
  getHighwayLocationV1,
  getIsPublicApiV1,
  (highwayLocationV0, highwayLocationV1, isApiPublicV1) => {
    const hasHighwayDirection = allPass([has('sectionCode'), has('countryCode'), has('highwayCode')]);
    if (isApiPublicV1) return hasHighwayDirection(highwayLocationV1);
    return hasHighwayDirection(highwayLocationV0);
  },
);
export const getFormatedAddress = createSelector(
  getClaimAddress,
  address => join(' ', without([undefined], values(address))),
);
export const getHighway = createSelector(
  getLocation,
  getLocationDetails,
  getIsPublicApiV1,
  (location, locationDetails, isApiPublicV1) => {
    if (isApiPublicV1) return location?.highway;
    return locationDetails?.highway;
  },
);
export const getCoveredGarage = createSelector(
  getLocation,
  getLocationDetails,
  getIsPublicApiV1,
  (location, locationDetails, isApiPublicV1) => {
    if (isApiPublicV1) return location?.coveredGarage;
    return locationDetails?.coveredGarage;
  },
);
export const getEvent = createSelector(
  getClaim,
  prop('event'),
);
export const getEventCode = createSelector(
  getEvent,
  prop('code'),
);
export const getEventDetails = createSelector(
  getClaim,
  getIsPublicApiV1,
  (claim, isApiPublicV1) => {
    if (isApiPublicV1) return claim?.event;
    return claim?.eventDetails;
  },
);
export const getNumberOfPassengers = createSelector(
  getEventDetails,
  prop('nbPersons'),
);
export const getFlatTire = createSelector(
  getEventDetails,
  prop('flatTire'),
);
export const getWindScreenDetails = createSelector(
  getEventDetails,
  prop('windscreenDetails'),
);
export const getSpareTire = createSelector(
  getEventDetails,
  prop('spareTire'),
);
export const getPersonSpecialNeeds = createSelector(
  getEventDetails,
  prop('personWithSpecialNeeds'),
);
export const getPersonInjured = createSelector(
  getEventDetails,
  prop('injuredPaxSubscriberCar'),
);
export const getOtherCarInvolved = createSelector(
  getEventDetails,
  prop('otherCarInvolved'),
);
export const getPersonInjuredCarInvolved = createSelector(
  getEventDetails,
  prop('injuredPax3rdPartyCar'),
);
export const getCarFuelType = createSelector(
  getEventDetails,
  getIsPublicApiV1,
  (eventDetails, isApiPublicV1) => (isApiPublicV1 ? prop('fuelType', eventDetails) : prop('carFuelType', eventDetails)),
);
export const getCarDrivable = createSelector(
  getEventDetails,
  prop('carDrivable'),
);
export const getCarFoundAfterTheft = createSelector(
  getEventDetails,
  prop('carFoundAfterTheft'),
);
export const getCarOffRoad = createSelector(
  getEventDetails,
  prop('carOffRoad'),
);
export const getCarUpsideDown = createSelector(
  getEventDetails,
  prop('carUpsideDown'),
);
export const getFilledFuelType = createSelector(
  getEventDetails,
  prop('filledFuelType'),
);
export const getVandalismDamages = createSelector(
  getEventDetails,
  prop('vandalismDamages'),
);
export const getFrontLeftTire = createSelector(
  getFlatTire,
  prop('frontLeft'),
);
export const getFrontRightTire = createSelector(
  getFlatTire,
  prop('frontRight'),
);
export const getRearLeftTire = createSelector(
  getFlatTire,
  prop('rearLeft'),
);
export const getRearRightTire = createSelector(
  getFlatTire,
  prop('rearRight'),
);
export const getWindScreenBroken = createSelector(
  getWindScreenDetails,
  prop('windscreenBroken'),
);
export const getRearWindScreenBroken = createSelector(
  getWindScreenDetails,
  prop('rearWindscreenBroken'),
);
export const getOtherWindowBroken = createSelector(
  getWindScreenDetails,
  prop('otherWindowBroken'),
);
export const getSunroofWindowBroken = createSelector(
  getWindScreenDetails,
  prop('sunroofWindowBroken'),
);
export const getDisasterType = createSelector(
  getEventDetails,
  prop('disaster'),
);
export const getDrivingLicenceRevocationDetails = createSelector(
  getEventDetails,
  prop('canVehicleBeDriven'),
);
export const getKeyBroken = createSelector(
  getEventDetails,
  prop('keyInSteeringLock'),
);
export const getVehicleCondition = createSelector(
  getEventDetails,
  prop('vehicleCondition'),
);
export const getVehiclePosition = createSelector(
  getEventDetails,
  prop('vehiclePosition'),
);
export const getVehiclePositionOffRoad = createSelector(
  getVehiclePosition,
  prop('offRoad'),
);
export const getVehiclePositionUpsideDown = createSelector(
  getVehiclePosition,
  prop('upsideDown'),
);
export const getKeyReplacement = createSelector(
  getEventDetails,
  prop('otherKeyLocation'),
);
export const getKeyReplacementAddress = createSelector(
  getKeyReplacement,
  prop('address'),
);
export const getKeyReplacementAddress1 = createSelector(
  getKeyReplacementAddress,
  prop('address1'),
);
export const getKeyReplacementCity = createSelector(
  getKeyReplacementAddress,
  prop('city'),
);
export const getKeyReplacementPostalCode = createSelector(
  getKeyReplacementAddress,
  prop('postalCode'),
);
export const getKeyReplacementCountry = createSelector(
  getKeyReplacementAddress,
  prop('country'),
);
export const getBreakdown = createSelector(
  getEventDetails,
  prop('breakdown'),
);
export const getFoundAfterTheft = createSelector(
  getEventDetails,
  prop('foundAfterTheft'),
);
export const getNumberOfFlatTires = createSelector(
  getEventDetails,
  prop('nbFlatTire'),
);
export const isClaimEventAlreadyPatched = createSelector(
  [getEvent, getEventDetails],
  (...patchedProp) => all(identity)(patchedProp),
);
export const getAffiliateCode = createSelector(
  getAffiliate,
  prop('code'),
);
export const getAffiliateCountry = createSelector(
  getAffiliate,
  prop('country'),
);

export const getProduct = createSelector(
  getSubscription,
  prop('product'),
);
export const getProductCode = createSelector(
  getProduct,
  prop('productCode'),
);

export const getBusinessPartnerCode = createSelector(
  getProduct,
  path(['businessPartner', 'code']),
);

export const getUncoveredEvents = createSelector(
  getProduct,
  prop('uncoveredEvent'),
);
export const getLimitDistance = createSelector(
  getCarDestinationsRules,
  getIsPublicApiV1,
  (rules, isPublicApiV1) =>
    isPublicApiV1 ? path(['limits', 'noFartherThanDistanceKm'], rules) : prop('limitDistance', rules),
);
export const getAllowCustomDestinationInDigital = createSelector(
  getCarDestinationsRules,
  getIsPublicApiV1,
  (rules, isPublicApiV1) =>
    isPublicApiV1
      ? path(['limits', 'allowCustomDestinationInDigital'], rules)
      : prop('allowCustomDestinationInDigital', rules),
);

export const getAllowNoDestinationProcessInDigital = createSelector(
  getCarDestinationsRules,
  getIsPublicApiV1,
  (rules, isPublicApiV1) =>
    isPublicApiV1
      ? path(['limits', 'allowNoDestinationProcessInDigital'], rules)
      : prop('allowNoDestinationProcessInDigital', rules),
);

export const getHasRecommendedNetworks = createSelector(
  getCarDestinationsRules,
  getIsPublicApiV1,
  (rules, isPublicApiV1) =>
    isPublicApiV1 ? path(['limits', 'hasRecommendedNetworks'], rules) : prop('hasRecommendedNetworks', rules),
);

export const getAllowHidingDestinationProcessInDigital = createSelector(
  getCarDestinationsRules,
  path(['limits', 'allowHidingDestinationProcessInDigital']),
);

export const getCustomDestinationDistanceLimit = createSelector(
  getCarDestinationsRules,
  getIsPublicApiV1,
  (rules, isPublicApiV1) =>
    isPublicApiV1 ? prop('maxDistance', rules) : prop('customDestinationDistanceLimit', rules),
);

export const getCustomDestinationDistanceLimitKm = createSelector(
  getCustomDestinationDistanceLimit,
  getIsPublicApiV1,
  (distanceLimit, isPublicApiV1) => (isPublicApiV1 ? prop('valueKm', distanceLimit) : prop('limitKm', distanceLimit)),
);

export const getIsCustomDestinationDistanceLimitKmPositive = createSelector(
  getCustomDestinationDistanceLimitKm,
  limitKm => lt(0)(limitKm) || isNil(limitKm),
);

export const getShowCustomDestination = createSelector(
  getAllowCustomDestinationInDigital,
  getIsCustomDestinationDistanceLimitKmPositive,
  (allowCustomDestinationInDigital, isCustomDestinationDistanceLimitKmPositive) =>
    allowCustomDestinationInDigital && isCustomDestinationDistanceLimitKmPositive,
);

export const getTask = path([ROOT, 'task']);
export const getTaskId = createSelector(
  getTask,
  prop('id'),
);
export const getTaskClaimId = createSelector(
  getTask,
  prop('claimId'),
);
export const getTaskMissionId = createSelector(
  getTask,
  prop('missionId'),
);
export const getTaskCreationDate = createSelector(
  getTask,
  prop('creationDate'),
);
export const getTaskCallbackNumber = createSelector(
  getTask,
  prop('callbackNumber'),
);
export const getTaskType = createSelector(
  getTask,
  prop('type'),
);
export const getTaskStatus = createSelector(
  getTask,
  prop('status'),
);
export const getTaskProduct = createSelector(
  getTask,
  prop('product'),
);
export const getTaskAffiliate = createSelector(
  getTask,
  prop('affiliate'),
);
export const getTaskAffiliateCode = createSelector(
  getTaskAffiliate,
  prop('code'),
);

export const getDriver = createSelector(
  getClaim,
  prop('driver'),
);
export const getPhoneMobile = createSelector(
  getDriver,
  getIsPublicApiV1,
  (driver, isApiPublicV1) => (isApiPublicV1 ? path(['contact', 'phoneMobile1'], driver) : prop('phoneMobile', driver)),
);
export const getFormattedPhoneNumber = createSelector(
  getPhoneMobile,
  phoneNumber => formatIntPhoneNumber(phoneNumber),
);
export const getFirstName = createSelector(
  getDriver,
  prop('firstName'),
);
export const getLastName = createSelector(
  getDriver,
  prop('lastName'),
);

export const getVehicle = createSelector(
  getClaim,
  prop('vehicle'),
);

export const getVehicleImmobilization = createSelector(
  getClaim,
  prop('vehicleImmobilization'),
);

const getGarageImmobilization = createSelector(
  getVehicleImmobilization,
  prop('garage'),
);

const getGarageImmobilizationLocation = createSelector(
  getGarageImmobilization,
  prop('location'),
);

export const getGarageImmobilizationAddress = createSelector(
  getGarageImmobilizationLocation,
  prop('address'),
);

export const getGarageImmobilizationFullAddress = createSelector(
  getGarageImmobilizationAddress,
  address => join(' ', without([undefined], values(address))),
);

export const getGarageImmobilizationPlaceName = createSelector(
  getGarageImmobilizationAddress,
  prop('placeName'),
);

export const getGarageImmobilizationAddress1 = createSelector(
  getGarageImmobilizationAddress,
  prop('address1'),
);

const getGarageImmobilizationCity = createSelector(
  getGarageImmobilizationAddress,
  prop('city'),
);

const getGarageImmobilizationPostalCode = createSelector(
  getGarageImmobilizationAddress,
  prop('postalCode'),
);

export const getGarageImmobilizationCityPostalCode = createSelector(
  getGarageImmobilizationCity,
  getGarageImmobilizationPostalCode,
  (city, postalCode) => join(' ', without([undefined], [city, postalCode])),
);

export const getGarageImmobilizationState = createSelector(
  getGarageImmobilizationAddress,
  prop('state'),
);

const getGarageImmobilizationContact = createSelector(
  getGarageImmobilization,
  prop('contact'),
);

export const getGarageImmobilizationName = createSelector(
  getGarageImmobilizationContact,
  prop('name'),
);

export const getGarageImmobilizationPhoneNumber = createSelector(
  getGarageImmobilizationContact,
  prop('phoneNumber'),
);

export const getVehicleImmobilizationRepairStart = createSelector(
  getVehicleImmobilization,
  prop('repairStart'),
);

export const getVehicleImmobilizationRepairDuration = createSelector(
  getVehicleImmobilization,
  prop('repairDuration'),
);

export const getVehicleImmobilizationLaborDuration = createSelector(
  getVehicleImmobilization,
  prop('laborDuration'),
);

export const getPlateNumber = createSelector(
  getVehicle,
  prop('plateNumber'),
);
export const getModel = createSelector(
  getVehicle,
  prop('model'),
);
export const getVehicleCountry = createSelector(
  getVehicle,
  prop('country'),
);
export const getVehicleType = createSelector(
  getVehicle,
  getModel,
  getIsPublicApiV1,
  (vehicle, model, isApiPublicV1) => (isApiPublicV1 ? prop('type', vehicle) : prop('type', model)),
);
export const getVehicleWeight = createSelector(
  getVehicle,
  getModel,
  getIsPublicApiV1,
  (vehicle, model, isApiPublicV1) => (isApiPublicV1 ? prop('weight', vehicle) : prop('weight', model)),
);
export const getVehicleMake = createSelector(
  getVehicle,
  getModel,
  getIsPublicApiV1,
  (vehicle, model, isApiPublicV1) => (isApiPublicV1 ? prop('make', vehicle) : path(['vehicleMake', 'make'], model)),
);

export const getVehicleModel = createSelector(
  getVehicle,
  getModel,
  getIsPublicApiV1,
  (vehicle, model, isApiPublicV1) => (isApiPublicV1 ? prop('model', vehicle) : prop('model', model)),
);

export const getEnergy = createSelector(
  getVehicle,
  path(['details', 'energy']),
);

export const getEnergyType = createSelector(
  getVehicle,
  path(['details', 'energyType']),
);

export const getVehicleRegistrationDate = createSelector(
  getVehicle,
  prop('registrationDate'),
);

export const getHighwaySections = createSelector(
  getCoreApi,
  prop('highwaySections'),
);
export const getAuth = applySpec({
  accessToken: getAccessToken,
  refreshToken: getRefreshToken,
  tokenType: getTokenType,
  scope: getScope,
  expiresAt: getExpiresAt,
});

export const getAllAddresses = createSelector(
  [getClaim, getMission],
  (claim, mission) => ({
    driversHome: path(['driver', 'homeLocation'], claim),
    eventLocation: prop('location', claim),
    providerLocation: path(['provider', 'location'], mission),
    vehicleDestination: path(['vehicleDestination', 'provider', 'location'], mission),
  }),
);

export const getScopeStatus = createCoreApiSelector(data => {
  const predicates = [
    {
      value: APPROVED,
      cond: hasScope(APPROVED),
    },
    {
      value: IDENTIFIED,
      cond: allPass([hasScope(IDENTIFIED), complement(hasScope(APPROVED))]),
    },
    {
      value: AUTHENTICATED,
      cond: allPass([hasScope(AUTHENTICATED), complement(hasScope(APPROVED)), complement(hasScope(IDENTIFIED))]),
    },
  ];
  return prop('value', find(({ cond }) => cond(data), predicates));
});

export const getProductConfiguration = path([ROOT, 'productConfiguration']);

export const getBenefit = path([ROOT, 'selectedBenefit']);

export const cscEligibility = path([ROOT, 'cscEligibility']);

export const getCscLocationBasedDigitalExists = createSelector(
  cscEligibility,
  prop('cscLocationBasedDigitalExits'),
);

export const getCscEligibilityMaximumRentalDays = createSelector(
  cscEligibility,
  prop('maximumRentalDays'),
);

export const rentalCurrentLimits = path([ROOT, 'rentalCurrentLimits']);

export const getCurrentLimitsRemainingRentalDays = createSelector(
  rentalCurrentLimits,
  prop('remainingRentalDays'),
);

export const getCscEligibilityWOFirstService = createSelector(
  cscEligibility,
  prop('allowsDigitalRentalWoFirstService'),
);
